import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../../app/services/hooks/useOrganizationService';
import useRefreshAuth from '../../../../hooks/useRefreshAuth';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import DefaultCard from '../../../../components/default/DefaultCard';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import InputField from '../../../../components/InputField';
import FileUpload from '../../../../components/FileUpload';
import { WHITE_LABEL_LABELS } from '../../types';
import { OrganizationContextProps } from '../index';

interface BrandingForm {
    display_name: string;
    primary_color: string;
    secondary_color: string;
    email_sender_name: string;
    email_reply_to: string;
    subdomain: string;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

const BrandingTab: React.FC<OrganizationContextProps> = ({ organization, membership, goToTab }) => {
    const { getBranding, updateBranding, fetchingBranding, fetchingUpdateBranding } = useOrganizationService();
    const { refreshAuth } = useRefreshAuth();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();

    const [form, setForm] = useState<BrandingForm | null>(null);
    const [level, setLevel] = useState<'none' | 'basic' | 'full'>('none');
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [removeLogo, setRemoveLogo] = useState(false);

    const canEdit = membership.is_admin && level !== 'none' && organization.status === 'active';
    const isFull = level === 'full';

    const applyResponse = (data: any) => {
        const branding = data.branding;
        setForm({
            display_name: branding.display_name,
            primary_color: branding.primary_color,
            secondary_color: branding.secondary_color,
            email_sender_name: branding.email_sender_name,
            email_reply_to: branding.email_reply_to,
            subdomain: branding.subdomain
        });
        setLogoUrl(branding.logo_url);
        setLevel(data.level);
    };

    useEffect(() => {
        getBranding({ onSuccess: (response) => applyResponse(response.data), onError: (r) => toast.error(r.message) });
    }, []);

    const set = (field: keyof BrandingForm) => (value: string) => form && setForm({ ...form, [field]: value });

    // Vista previa del logo elegido; la URL temporal se libera al cambiarlo
    useEffect(() => {
        if (!logoFile) {
            setLogoPreview(null);
            return;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const handleSave = () => {
        if (!form) return;
        const data = new FormData();
        Object.entries(form).forEach(([key, value]) => data.append(key, value ?? ''));
        if (logoFile) data.append('logo', logoFile);
        if (removeLogo) data.append('remove_logo', 'true');
        updateBranding(data, {
            onSuccess: (response) => {
                toast.success(response.message);
                setErrors({});
                setLogoFile(null);
                setRemoveLogo(false);
                applyResponse(response.data);
                // La interfaz toma la marca nueva sin volver a iniciar sesión
                refreshAuth();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => {
                setErrors(response.data);
                toast.error('Revisa los campos marcados');
            }
        });
    };

    if (fetchingBranding && !form) {
        return <DefaultCard><div className="text-center py-4"><div className="spinner-border text-primary" /></div></DefaultCard>;
    }
    if (!form) return null;

    const primary = HEX.test(form.primary_color) ? form.primary_color : '#001849';
    const secondary = HEX.test(form.secondary_color) ? form.secondary_color : '#003d82';
    const previewLogo = logoFile ? logoPreview : removeLogo ? null : logoUrl;

    return (
        <div className="row">
            <div className="col-lg-7 mb-3">
                <DefaultCard>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h5 className="mb-1">Marca del estudio</h5>
                            <p className="text-muted small mb-0">
                                Tus clientes ven la plataforma y reciben los correos con tu marca. Tu plan:{' '}
                                <strong>{WHITE_LABEL_LABELS[level]}</strong>.
                            </p>
                        </div>
                    </div>

                    {level === 'none' && (
                        <div className="alert alert-info small">
                            Tu plan no incluye personalización de marca: tus clientes ven Monere.{' '}
                            {membership.is_admin && (
                                <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => goToTab('subscription')}>
                                    Ver planes con marca propia
                                </button>
                            )}
                        </div>
                    )}

                    <div className="row">
                        <div className="col-12 mb-3">
                            <InputField label="Nombre visible" value={form.display_name} disabled={!canEdit}
                                        placeholder={organization.name} onChange={set('display_name')}
                                        onFocus={() => onFocusRemove('display_name')} error={fieldErrorMessage('display_name')} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <InputField label="Color principal (#RRGGBB)" value={form.primary_color} disabled={!canEdit}
                                        onChange={set('primary_color')} onFocus={() => onFocusRemove('primary_color')}
                                        error={fieldErrorMessage('primary_color')} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <InputField label="Color secundario (#RRGGBB)" value={form.secondary_color} disabled={!canEdit}
                                        onChange={set('secondary_color')} onFocus={() => onFocusRemove('secondary_color')}
                                        error={fieldErrorMessage('secondary_color')} />
                        </div>
                        <div className="col-12 mb-3">
                            <label className="form-label fw-bold small">Logo (PNG, JPG, WEBP o SVG, máx. 2 MB)</label>
                            <FileUpload
                                onFileSelect={(file) => {
                                    setLogoFile(file);
                                    setRemoveLogo(false);
                                }}
                                currentFile={logoFile}
                                existingFileUrl={removeLogo ? null : logoUrl}
                                existingFileName="Logo actual"
                                acceptedTypes=".png,.jpg,.jpeg,.webp,.svg"
                                maxSize={2}
                                showPreview
                                disabled={!canEdit}
                            />
                            {fieldErrorMessage('logo') && <small className="text-danger">{fieldErrorMessage('logo')}</small>}
                            {logoUrl && canEdit && !removeLogo && (
                                <button type="button" className="btn btn-link btn-sm text-danger p-0 mt-1" onClick={() => setRemoveLogo(true)}>
                                    Quitar logo
                                </button>
                            )}
                        </div>

                        <div className="col-12"><hr /><h6 className="mb-1">Correo y dominio</h6>
                            <p className="text-muted small">
                                {isFull
                                    ? 'Los correos salen con este nombre de remitente y las respuestas llegan a tu correo.'
                                    : 'Disponible en el plan Estudio (marca completa).'}
                            </p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <InputField label="Nombre del remitente" value={form.email_sender_name} disabled={!canEdit || !isFull}
                                        onChange={set('email_sender_name')} onFocus={() => onFocusRemove('email_sender_name')}
                                        error={fieldErrorMessage('email_sender_name')} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <InputField label="Responder a" type="email" value={form.email_reply_to} disabled={!canEdit || !isFull}
                                        placeholder={organization.email} onChange={set('email_reply_to')}
                                        onFocus={() => onFocusRemove('email_reply_to')} error={fieldErrorMessage('email_reply_to')} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <InputField label="Subdominio" value={form.subdomain} disabled={!canEdit || !isFull}
                                        placeholder="mi-estudio" onChange={set('subdomain')}
                                        onFocus={() => onFocusRemove('subdomain')} error={fieldErrorMessage('subdomain')} />
                            <small className="text-muted">Quedará como {form.subdomain || 'mi-estudio'}.monere.cl cuando Monere lo active.</small>
                        </div>
                    </div>

                    {canEdit && (
                        <div className="d-flex justify-content-end">
                            <ButtonSaveForm callbackSave={handleSave} loading={fetchingUpdateBranding} title="Guardar marca" />
                        </div>
                    )}
                </DefaultCard>
            </div>

            <div className="col-lg-5 mb-3">
                <DefaultCard>
                    <h6 className="text-muted text-uppercase small mb-3">Vista previa</h6>
                    <div className="rounded overflow-hidden border">
                        <div className="p-3 text-center" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}>
                            {previewLogo ? (
                                <img src={previewLogo} alt="Logo" style={{ maxHeight: 56, maxWidth: 200, background: '#fff', borderRadius: 6, padding: 6 }} />
                            ) : (
                                <span className="text-white small text-uppercase">{form.display_name || organization.name}</span>
                            )}
                            <div className="text-white fw-bold mt-2">Tu solicitud fue creada</div>
                        </div>
                        <div className="p-3 small">
                            <p className="mb-2" style={{ color: primary }}><strong>Hola María,</strong></p>
                            <p className="text-muted mb-3">Así se verán los correos y el portal de tus clientes.</p>
                            <span className="btn btn-sm text-white" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}>
                                Ver mi solicitud
                            </span>
                        </div>
                    </div>
                </DefaultCard>
            </div>
        </div>
    );
};

export default BrandingTab;
